// src/pages/SnagBoard/components/CreateSnagModal.tsx

import React, { useState } from 'react';
import { 
  FiX, 
  FiCamera, 
  FiPaperclip, 
  FiMapPin, 
  FiCalendar,
  FiAlertTriangle,
  FiPlus,
  FiTrash2,
  FiTag
} from 'react-icons/fi';
import { Snag } from '../../../shared/types/snag';
import { useToast } from '../../../components/toast/toastProvider';
import SnagTrackerService from '../../../services/snagTrackerService';
import './CreateSnagModal.css';

interface CreateSnagModalProps {
  onClose: () => void;
  onSubmit: (snag: Snag) => void;
}

const PRIORITY_OPTIONS = ['Critical', 'High', 'Medium', 'Low'];

const ASSIGNEE_OPTIONS = [
  { id: '1', name: 'John Smith', trade: 'Tiling' },
  { id: '3', name: 'Mike Chen', trade: 'Painting' },
  { id: '5', name: 'Tom Anderson', trade: 'Electrical' },
  { id: '7', name: 'Sarah Johnson', trade: 'Plumbing' },
  { id: '9', name: 'David Wilson', trade: 'Carpentry' }
];

const TAG_SUGGESTIONS = [
  'Electrical', 'Plumbing', 'Tiling', 'Painting', 'Carpentry',
  'HVAC', 'Safety', 'Finishing', 'Structural', 'Flooring'
];

const CreateSnagModal: React.FC<CreateSnagModalProps> = ({ onClose, onSubmit }) => {
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    priority: 'Medium' as Snag['priority'],
    assignedTo: '',
    building: '',
    floor: '',
    room: '',
    area: '',
    dueDate: '',
    tags: [] as string[],
    photos: [] as File[],
    attachments: [] as File[]
  });

  const [newTag, setNewTag] = useState('');
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { addToast } = useToast();

  const handleInputChange = (field: string, value: any) => {
    setFormData({ ...formData, [field]: value });
    if (errors[field]) {
      setErrors({ ...errors, [field]: '' });
    }
  };

  const handleFileUpload = (field: 'photos' | 'attachments', files: FileList | null) => {
    if (files) {
      const fileArray = Array.from(files);
      setFormData({ ...formData, [field]: [...formData[field], ...fileArray] });
    }
  };

  const removeFile = (field: 'photos' | 'attachments', index: number) => {
    const updatedFiles = formData[field].filter((_, i) => i !== index);
    setFormData({ ...formData, [field]: updatedFiles });
  };

  const addTag = () => {
    const trimmed = newTag.trim();
    if (trimmed && !formData.tags.includes(trimmed)) {
      setFormData({ ...formData, tags: [...formData.tags, trimmed] });
      setNewTag('');
    }
  };

  const removeTag = (tag: string) => {
    setFormData({ ...formData, tags: formData.tags.filter(t => t !== tag) });
  };

  const addSuggestedTag = (tag: string) => {
    if (!formData.tags.includes(tag)) {
      setFormData({ ...formData, tags: [...formData.tags, tag] });
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    if (!formData.title.trim()) newErrors.title = 'Title is required';
    if (!formData.description.trim()) newErrors.description = 'Description is required';
    if (!formData.assignedTo) newErrors.assignedTo = 'Please assign to someone';
    if (!formData.building) newErrors.building = 'Building is required';
    if (!formData.area.trim()) newErrors.area = 'Area is required';
    if (formData.dueDate) {
      const selectedDate = new Date(formData.dueDate);
      const today = new Date();
      today.setHours(0,0,0,0);
      if (selectedDate < today) newErrors.dueDate = 'Due date cannot be in the past';
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateForm()) {
      addToast({ type: 'error', title: 'Validation Error', message: 'Please fix errors and try again.', duration: 4000 });
      return;
    }
    setIsSubmitting(true);

    try {
      // Create snag via API
      const newSnag = await SnagTrackerService.createSnag({
        title: formData.title.trim(),
        description: formData.description.trim(),
        priority: formData.priority,
        assignedTo: formData.assignedTo as any,
        location: `${formData.building}${formData.floor ? ` - ${formData.floor}` : ''}${formData.room ? ` - ${formData.room}` : ''}${formData.area ? ` - ${formData.area}` : ''}`,
        tags: formData.tags,
        dueDate: formData.dueDate || undefined
      });

      await Promise.all([
        ...formData.photos.map(file => SnagTrackerService.addAttachment(newSnag.id, file)),
        ...formData.attachments.map(file => SnagTrackerService.addAttachment(newSnag.id, file)),
      ]);

      onSubmit(newSnag);
      addToast({
        type: 'success',
        title: 'Snag Created',
        message: `"${newSnag.title}" has been created successfully`,
        duration: 4000
      });
    } catch (err) {
      console.error('Failed to create snag:', err);
      addToast({
        type: 'error',
        title: 'Submission Failed',
        message: err instanceof Error ? err.message : 'Could not create snag, please try again.'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="create-snag-overlay" role="dialog" aria-modal="true" aria-labelledby="create-snag-title">
      <div className="create-snag-modal">
        <div className="modal-header">
          <h2 id="create-snag-title">Create New Snag</h2>
          <button onClick={onClose} disabled={isSubmitting} aria-label="Close create snag form" className="close-btn"><FiX size={20} /></button>
        </div>

        <div className="modal-body">
          <form id="snagForm" onSubmit={handleSubmit} className="modal-form">

            {/* Basic Information */}
            <div className="form-section">
              <h3><FiAlertTriangle /> Basic Information</h3>
              <div className="form-row">
                <div className="form-group full-width">
                  <label>Title *</label>
                  <input
                    type="text" value={formData.title} onChange={e => handleInputChange('title', e.target.value)}
                    disabled={isSubmitting} className={errors.title ? 'error' : ''} placeholder="Brief summary"
                  />
                  {errors.title && <small className="error-message">{errors.title}</small>}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group full-width">
                  <label>Description *</label>
                  <textarea
                    value={formData.description} onChange={e => handleInputChange('description', e.target.value)}
                    disabled={isSubmitting} className={errors.description ? 'error' : ''} rows={4} placeholder="Detailed description"
                  />
                  {errors.description && <small className="error-message">{errors.description}</small>}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Priority *</label>
                  <select value={formData.priority} onChange={e => handleInputChange('priority', e.target.value as Snag['priority'])} disabled={isSubmitting}>
                    {PRIORITY_OPTIONS.map(p => (<option key={p} value={p}>{p}</option>))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Assign To *</label>
                  <select value={formData.assignedTo} onChange={e => handleInputChange('assignedTo', e.target.value)} disabled={isSubmitting} className={errors.assignedTo ? 'error' : ''}>
                    <option value="">Select person</option>
                    {ASSIGNEE_OPTIONS.map(a => (<option key={a.id} value={a.id}>{a.name} - {a.trade}</option>))}
                  </select>
                  {errors.assignedTo && <small className="error-message">{errors.assignedTo}</small>}
                </div>
              </div>
            </div>

            {/* Location */}
            <div className="form-section">
              <h3><FiMapPin /> Location</h3>
              <div className="form-row">
                <div className="form-group">
                  <label>Building *</label>
                  <select value={formData.building} onChange={e => handleInputChange('building', e.target.value)} disabled={isSubmitting} className={errors.building ? 'error' : ''}>
                    <option value="">Select building</option>
                    {['Block A', 'Block B', 'Block C', 'Common Areas'].map(b => <option key={b} value={b}>{b}</option>)}
                  </select>
                  {errors.building && <small className="error-message">{errors.building}</small>}
                </div>
                <div className="form-group">
                  <label>Floor</label>
                  <select value={formData.floor} onChange={e => handleInputChange('floor', e.target.value)} disabled={isSubmitting}>
                    <option value="">Select floor</option>
                    {['Ground Floor', '1st Floor', '2nd Floor', '3rd Floor'].map(f => <option key={f} value={f}>{f}</option>)}
                  </select>
                </div>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Room</label>
                  <select value={formData.room} onChange={e => handleInputChange('room', e.target.value)} disabled={isSubmitting}>
                    <option value="">Select room</option>
                    {['Living Room', 'Kitchen', 'Bathroom', 'Bedroom', 'Hallway', 'Balcony'].map(r => <option key={r} value={r}>{r}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label>Specific Area *</label>
                  <input
                    type="text" value={formData.area} onChange={e => handleInputChange('area', e.target.value)} disabled={isSubmitting} placeholder="e.g., 2A-Bath-01" className={errors.area ? 'error' : ''}
                  />
                  {errors.area && <small className="error-message">{errors.area}</small>}
                </div>
              </div>
            </div>

            {/* Schedule */}
            <div className="form-section">
              <h3><FiCalendar /> Schedule</h3>
              <div className="form-row">
                <div className="form-group">
                  <label>Due Date</label>
                  <input type="date" value={formData.dueDate} onChange={e => handleInputChange('dueDate', e.target.value)} disabled={isSubmitting} className={errors.dueDate ? 'error' : ''}/>
                  {errors.dueDate && <small className="error-message">{errors.dueDate}</small>}
                </div>
              </div>
            </div>

            {/* Tags */}
            <div className="form-section">
              <h3><FiTag /> Tags</h3>
              <div className="tags-input">
                <div className="add-tag-input">
                  <input
                    type="text" value={newTag} onChange={e => setNewTag(e.target.value)}
                    placeholder="Add a tag" disabled={isSubmitting}
                    onKeyPress={e => { if(e.key === 'Enter'){ e.preventDefault(); addTag(); } }}
                  />
                  <button type="button" onClick={addTag} disabled={isSubmitting} className="add-tag-btn">
                    <FiPlus />
                  </button>
                </div>
                {formData.tags.length > 0 && (
                  <div className="selected-tags">
                    {formData.tags.map(t => (
                      <span className="tag selected" key={t}>
                        {t}
                        <button type="button" onClick={() => removeTag(t)} disabled={isSubmitting}><FiX /></button>
                      </span>
                    ))}
                  </div>
                )}

                <div className="suggested-tags">
                  <span className="suggestions-label">Suggestions:</span>
                  {TAG_SUGGESTIONS.filter(t => !formData.tags.includes(t)).map(t => (
                    <button type="button" className="tag suggestion" key={t} onClick={() => addSuggestedTag(t)} disabled={isSubmitting}>
                      {t}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Attachments & Photos Upload */}
            <div className="form-section">
              <h3>Attachments & Photos</h3>
              <div className="file-upload-section">
                <div className="upload-group">
                  <label htmlFor="photos" className="upload-label">
                    <FiCamera /> Photos
                  </label>
                  <input type="file" id="photos" multiple accept="image/*" onChange={e => handleFileUpload('photos', e.target.files)} disabled={isSubmitting} className="file-input"/>
                  {formData.photos.length > 0 && (
                    <div className="file-list">
                      {formData.photos.map((file, idx) => (
                        <div key={idx} className="file-item">
                          <span>{file.name}</span>
                          <button type="button" onClick={() => removeFile('photos', idx)} disabled={isSubmitting}><FiTrash2 /></button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="upload-group">
                  <label htmlFor="attachments" className="upload-label">
                    <FiPaperclip /> Documents
                  </label>
                  <input type="file" id="attachments" multiple onChange={e => handleFileUpload('attachments', e.target.files)} disabled={isSubmitting} className="file-input" />
                  {formData.attachments.length > 0 && (
                    <div className="file-list">
                      {formData.attachments.map((file, idx) => (
                        <div key={idx} className="file-item">
                          <span>{file.name}</span>
                          <button type="button" onClick={() => removeFile('attachments', idx)} disabled={isSubmitting}><FiTrash2 /></button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </div>
          </form>
        </div>

        <div className="modal-footer">
          <button type="button" onClick={onClose} className="cancel-btn" disabled={isSubmitting}>Cancel</button>
          <button type="submit" form="snagForm" className="submit-btn" disabled={isSubmitting}>
            {isSubmitting ? 'Creating...' : 'Create Snag'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateSnagModal;
